/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import type { I18nLike } from '@bingads-webui/common-types';
import { Input } from '@bingads-webui-react/primitive-validation';

export type PrimitiveValidationProps = NonNullable<React.ComponentProps<typeof Input>['validationProps']>;
export type PrimitiveValidationResult = Parameters<NonNullable<PrimitiveValidationProps['onValidate']>>[0];

export interface AutoCompleteValue {
  email?: string;
  [field: string]: string | undefined;
}

export interface AutoCompleteOption {
  displayText: string;
  value: AutoCompleteValue;
}

export type AutoCompleteValidationProps = Omit<
PrimitiveValidationProps,
  'data' | 'field' | 'i18n' | 'onValidate' | 'schema'
> & {
  data: Record<string, string>;
  field: string;
  alwaysShowError?: boolean;
  i18n?: I18nLike;
  onValidate?: (result: PrimitiveValidationResult) => void;
  schema?: PrimitiveValidationProps['schema'];
  scheme?: PrimitiveValidationProps['schema'];
};

export interface InputWithAutoCompleteProps {
  dropdownOptions?: AutoCompleteOption[];
  className?: string;
  validationProps: AutoCompleteValidationProps;
  onSeleteChange?: ((value: AutoCompleteValue) => void) | null;
  letterTriggerCount?: number;
}

export function InputWithAutoComplete(props: InputWithAutoCompleteProps) {
  const {
    dropdownOptions,
    className,
    validationProps,
    onSeleteChange,
    letterTriggerCount,
  } = props;

  const {
    onValidate,
    field,
    data,
    i18n,
    schema,
    alwaysShowError,
  } = validationProps || {};

  const [key, setKey] = React.useState(0);
  const [inputValue, setInputValue] = useState(data[field] ? data[field] : '');

  const onAutoCompleteValidate = (inputProps: PrimitiveValidationResult) => {
    // do some string validation
    if (_.isFunction(onValidate)) {
      onValidate(inputProps);
    }

    // get the choosed option
    const newInputValue = inputProps.data as Record<string, string>;
    const choosedOption = _.find(dropdownOptions, (option: AutoCompleteOption) => option.displayText === newInputValue[field]);

    if (!_.isObject(choosedOption)) {
      // if the input value in not one of the option
      setInputValue(newInputValue[field]);
    } else if (_.isFunction(onSeleteChange) && inputValue !== choosedOption.value.email) {
      // if the input value in one of the option
      onSeleteChange(choosedOption.value);
      setInputValue(choosedOption.value.email);
    } else {
      // if the input value is already be setted, need to setKey() to trigger reRender for input
      onSeleteChange(choosedOption.value);
      setKey((preKey: number) => preKey + 1);
    }
  };

  return (
    <div>
      <Input
        key={key}
        className={className}
        list={letterTriggerCount <= inputValue.length ? 'options-list' : ''}
        validationProps={{
          onValidate: onAutoCompleteValidate,
          field,
          data: { [field]: inputValue },
          i18n,
          schema,
          alwaysShowError,
        }}
      />
      <datalist id="options-list">
        {_.map(dropdownOptions, (option: AutoCompleteOption, id: number) => <option key={id} value={option.displayText} />)}
      </datalist>
    </div>
  );
}

InputWithAutoComplete.propTypes = {
  dropdownOptions: PropTypes.arrayOf(PropTypes.shape({
    displayText: PropTypes.string,
    value: PropTypes.objectOf(PropTypes.any),
  })),
  className: PropTypes.string,
  validationProps: PropTypes.objectOf(PropTypes.any).isRequired,
  onSeleteChange: PropTypes.func,
  letterTriggerCount: PropTypes.number,
};

InputWithAutoComplete.defaultProps = {
  dropdownOptions: [],
  className: '',
  onSeleteChange: null,
  letterTriggerCount: 0,
};
